// Code Block: a chunk of code with a title, line numbers, a copy button and a height toggle.
//
// No syntax highlighting. The Ember component runs Prism.js with eight language grammars
// and two plugins; that is a bundle of its own, and a product that already highlights its
// code server-side (Pygments, Shiki, Rouge) would fight it. So the code is rendered exactly
// as it is written, and `language` only adds the `language-<name>` class Prism's theme keys
// off, so markup a highlighter produced can be dropped in later without changing the box.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

const LANGUAGES = ["bash", "go", "hcl", "json", "log", "ruby", "shell-session", "yaml"];
const TITLE_TAGS = ["p", "h1", "h2", "h3", "h4", "h5", "h6"];
const TITLE_CLASS = "i80-text i80-typography-body-200 i80-font-weight-semibold i80-code-block__title";
const DESCRIPTION_CLASS = "i80-text i80-typography-body-100 i80-font-weight-regular i80-code-block__description";

/** Our own part inside `parent`, never one belonging to a nested <i80-*> child. */
const own = (parent, name) => parent.querySelector(`:scope > [data-i80-part="${name}"]`);

/**
 * The code without the indentation the surrounding HTML added: an example written inside
 * a tag is indented like the rest of the page, and that indentation is not part of the code.
 * The blank first and last lines the tag's own line breaks leave behind go too.
 */
function dedent(text) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  while (lines.length && !lines[0].trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  let prefix = null;
  for (const line of lines) {
    if (!line.trim()) continue;
    const indent = line.match(/^[ \t]*/)[0];
    if (prefix === null) {
      prefix = indent;
      continue;
    }
    let i = 0;
    while (i < prefix.length && prefix[i] === indent[i]) i += 1;
    prefix = prefix.slice(0, i);
  }
  if (!prefix) return lines.join("\n");
  return lines.map((l) => (l.startsWith(prefix) ? l.slice(prefix.length) : l.replace(/^[ \t]+/, ""))).join("\n");
}

/** The gutter Prism's line-numbers plugin draws: one empty span per line, numbered by CSS. */
function lineNumberRows(count) {
  const rows = h("span", { "aria-hidden": "true", class: "line-numbers-rows" });
  for (let i = 0; i < count; i += 1) rows.append(h("span"));
  return rows;
}

/**
 * <i80-code-block title="Create the cluster" language="bash" copy-button max-height="200px">
 *   terraform apply -auto-approve
 * </i80-code-block>
 *
 * The code is the text inside the tag (its indentation is removed, so the example can sit
 * where it belongs in your HTML) or the `value` attribute for a single line. Line numbers
 * are on; with `max-height` the box scrolls and a button expands it. The copy button is
 * the <i80-copy-button> element, copying the <pre> the same way the Ember one does.
 */
export class I80CodeBlock extends I80Element {
  static get observedAttributes() {
    return ["value", "title", "title-tag", "description", "language", "line-numbers", "line-number-start",
      "line-wrapping", "standalone", "max-height", "copy-button", "copy-button-text", "copied-message",
      "label", "labelled-by", "described-by"];
  }

  setup() {
    let root = this.part("code-block");
    if (root) {
      // A clone: the code is the text node we wrote, in front of the line-number gutter.
      this._raw = own(own(root, "body"), "pre").firstChild.firstChild?.nodeValue ?? "";
    } else {
      this._collect();
      root = h("div", { class: "i80-code-block", "data-i80-part": "code-block" },
        h("div", { class: "i80-code-block__header", "data-i80-part": "header" }),
        h("div", { class: "i80-code-block__body", "data-i80-part": "body" },
          h("pre", { class: "i80-code-block__code", tabindex: "0", "data-i80-part": "pre" }, h("code"))));
      this.append(root);
    }
    this._root = root;
    this._header = own(root, "header");
    this._body = own(root, "body");
    this._pre = own(this._body, "pre");
    this._codeEl = this._pre.firstChild;
    // New ids on every instance, so a clone never points at the original's code.
    this._pre.id = nextId("i80-code-block");
    this._expanded = false;
    this._overflows = false;
    this._ids = {};

    // Both the code and the box around it can change size after the first paint (a font
    // arrives, the page reflows, a tab it sits in becomes visible), and whether the height
    // toggle is needed depends on exactly that.
    this._sizes = new ResizeObserver(() => this._measure());
    this._sizes.observe(this._pre);
  }

  /** Text the author wrote in the tag is the code; it can also arrive after we are built. */
  _collect() {
    const nodes = this.takeContent();
    if (!nodes.length) return false;
    this._raw = (this._raw ?? "") + nodes.map((n) => n.textContent).join("");
    for (const n of nodes) n.remove();
    return true;
  }

  adopt() {
    this._collect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._sizes?.disconnect();
  }

  connectedCallback() {
    super.connectedCallback();
    if (this._pre) this._sizes.observe(this._pre);
  }

  /** The code as it will be rendered and copied. */
  get code() {
    return this.getAttribute("value") ?? dedent(this._raw ?? "");
  }

  get expanded() {
    return this._expanded;
  }

  set expanded(value) {
    if (this._expanded === !!value) return;
    this._expanded = !!value;
    this.update();
  }

  /** Show the whole block, or shrink it back to max-height. */
  toggle() {
    this.expanded = !this._expanded;
    this.dispatchEvent(new CustomEvent("i80-toggle", { bubbles: true, detail: { expanded: this._expanded } }));
  }

  update() {
    const code = this.code;
    const lineNumbers = (this.getAttribute("line-numbers") ?? "true") !== "false";
    const standalone = (this.getAttribute("standalone") ?? "true") !== "false";
    const language = this.hasAttribute("language") ? this.choice("language", LANGUAGES, "") : "";

    this._codeEl.replaceChildren(document.createTextNode(code), ...(lineNumbers ? [lineNumberRows(code.split("\n").length)] : []));

    const start = this.getAttribute("line-number-start");
    this._pre.style.counterReset = start ? `linenumber ${Number(start) - 1}` : "";
    if (start) this._pre.setAttribute("data-start", start);
    else this._pre.removeAttribute("data-start");
    this._pre.style.maxHeight = this._expanded ? "none" : (this.getAttribute("max-height") ?? "");

    this._titles();
    this._label();
    this._copyButton();
    this._footer();

    this._root.className = [
      "i80-code-block",
      "i80-code-block--theme-dark",
      language && `language-${language}`,
      standalone && "i80-code-block--is-standalone",
      this.flag("line-wrapping") && "i80-code-block--has-line-wrapping",
      lineNumbers && "line-numbers",
      this._overflows && "i80-code-block--has-overlay-footer",
      this._expanded && "i80-code-block--is-expanded",
    ].filter(Boolean).join(" ");
  }

  /** The title and the description, in the header; an empty header styles itself away. */
  _titles() {
    const line = (name, tag, cls) => {
      own(this._header, name)?.remove();
      const text = this.getAttribute(name);
      if (!text) return null;
      this._ids[name] ??= nextId(`i80-code-block-${name}`);
      return h(tag, { class: cls, id: this._ids[name], "data-i80-part": name }, text);
    };
    const title = line("title", this.choice("title-tag", TITLE_TAGS, "p"), TITLE_CLASS);
    const description = line("description", "p", DESCRIPTION_CLASS);
    this._header.replaceChildren(...[title, description].filter(Boolean));
  }

  /**
   * The <pre> is the code: a scrollable, focusable box. aria-label on a bare <pre> is
   * prohibited (it names nothing), so a labelled one becomes a group — which is what it is.
   */
  _label() {
    const pre = this._pre;
    const label = this.getAttribute("label");
    const labelledBy = this.getAttribute("labelled-by") ?? own(this._header, "title")?.id;
    const describedBy = this.getAttribute("described-by") ?? own(this._header, "description")?.id;
    const set = (name, value) => (value ? pre.setAttribute(name, value) : pre.removeAttribute(name));
    set("aria-label", label);
    set("aria-labelledby", label ? null : labelledBy);
    set("aria-describedby", describedBy);
    set("role", label || labelledBy ? "group" : null);
  }

  _copyButton() {
    let copy = own(this._body, "copy");
    if (!this.flag("copy-button")) {
      copy?.remove();
      return;
    }
    const text = this.getAttribute("copy-button-text") ?? "Copy";
    if (!copy) {
      copy = h("i80-copy-button", { "icon-only": "", size: "small", text, "data-i80-part": "copy" },
        h("button", { type: "button", class: "i80-code-block__copy-button" }));
      this._body.append(copy);
    }
    copy.setAttribute("text", text);
    copy.setAttribute("target-to-copy", `#${this._pre.id}`);
    const message = this.getAttribute("copied-message");
    if (message) copy.setAttribute("copied-message", message);
    copy.querySelector("button")?.setAttribute("aria-describedby", this._pre.id);
  }

  _footer() {
    const footer = own(this._root, "footer");
    if (!this._overflows) {
      footer?.remove();
      return;
    }
    const open = this._expanded;
    const button = h("button", {
      type: "button",
      class: "i80-button i80-button--color-secondary i80-button--size-small i80-code-block__height-toggle-button",
    }, h("span", { class: "i80-button__icon" }, icon(open ? "unfold-close" : "unfold-open", { stretched: true })),
      h("span", { class: "i80-button__text" }, open ? "Show less code" : "Show more code"));
    button.addEventListener("click", () => this.toggle());
    if (footer) footer.replaceChildren(button);
    else this._root.append(h("div", { class: "i80-code-block__overlay-footer", "data-i80-part": "footer" }, button));
  }

  /**
   * Whether the code is taller than the box it is allowed to fill. Measured only while
   * collapsed: once expanded, the toggle has to stay so you can collapse it again.
   */
  _measure() {
    if (this._expanded || !this.isConnected) return;
    const overflows = !!this.getAttribute("max-height") && this._pre.scrollHeight > this._pre.clientHeight + 1;
    if (overflows === this._overflows) return;
    this._overflows = overflows;
    this.update();
  }
}

define("i80-code-block", I80CodeBlock);
